import { useEffect, useLayoutEffect, useRef } from "react";
import { getWindowRoleSync } from "@/desktop/use-window-role";
import type { FrameData } from "@/lib/frame-data";
import { useProjectStore } from "@/stores/project-store";
import { useInlineFrameEmbed } from "@/components/shapes/shared/inline-frame-embed-context";
import { sha256Hex } from "@/lib/sha256-hex";
import { captureHtmlPoster } from "./html-poster-capture";
import { hasThemePoster, usePosterTheme } from "./html-poster-theme";

/** A page settles this long after it mounts before its poster is taken. */
const CAPTURE_DELAY_MS = 2000;
/** A capture that may work later (no answer, network) is tried once more. */
const MAX_ATTEMPTS = 2;

/**
 * Captures by `frame:theme:content hash` in this session: running, ended
 * for good (stored or rejected), and how often each one asked for a retry.
 */
const running = new Set<string>();
const ended = new Set<string>();
const retries = new Map<string, number>();

interface HtmlPosterCaptureOptions {
  frameId: string;
  data: FrameData;
  /** The `content.html` the frame renders. */
  html: string;
  /** The page is a cross-origin embed, not a srcdoc document. */
  crossOriginEmbed: boolean;
  /** Inline editing is on: the document differs from the stored page. */
  editing: boolean;
  /** The frame's srcdoc iframe, when mounted. */
  getIframe: () => HTMLIFrameElement | null | undefined;
  /**
   * This page version's capture ended for good (stored, rejected or out of
   * tries), here or earlier in this session, or can't run here: no open
   * project, no iframe, or another frame already captures it.
   */
  onEnded?: () => void;
}

/**
 * Captures the poster and cover of a mounted HTML frame for the current card
 * theme when it has none, or only a poster without its cover: in the owner
 * window only, 2 s after the page mounts, not while it streams, is edited or
 * is a chat embed or cross-origin embed. Each frame, theme and page version
 * is captured until it is stored or rejected, with one more try after a
 * capture that may work later.
 */
export function useHtmlPosterCapture({
  frameId,
  data,
  html,
  crossOriginEmbed,
  editing,
  getIframe,
  onEnded,
}: HtmlPosterCaptureOptions): void {
  const theme = usePosterTheme();
  const inlineEmbed = useInlineFrameEmbed();
  const endedRef = useRef(onEnded);
  useLayoutEffect(() => {
    endedRef.current = onEnded;
  }, [onEnded]);
  const eligible =
    Boolean(html) &&
    !hasThemePoster(data.metadata?.thumbnail, theme) &&
    !crossOriginEmbed &&
    !inlineEmbed &&
    !editing &&
    data.status !== "streaming" &&
    data.contentState !== "card";

  useEffect(() => {
    if (!eligible) return;
    let cancelled = false;
    let timer: number | undefined;
    const attempt = async () => {
      if (getWindowRoleSync() !== "owner") return;
      // Frame API calls are scoped to the open project: a legacy frame's
      // stored project can be empty (its `spaceId` is then a Space id).
      const projectId = useProjectStore.getState().currentProject?.id;
      const contentSha256 = await sha256Hex(html);
      const key = `${frameId}:${theme}:${contentSha256}`;
      const iframe = getIframe();
      if (cancelled) return;
      // Nothing to capture here (no project, no page, or another frame
      // already captures this version): a queue waiting on it moves on.
      if (!projectId || !iframe || ended.has(key) || running.has(key)) {
        endedRef.current?.();
        return;
      }
      running.add(key);
      const outcome = await captureHtmlPoster({
        iframe,
        projectId,
        frameId,
        theme,
        contentSha256,
      });
      running.delete(key);
      const tries = (retries.get(key) ?? 0) + 1;
      if (outcome === "retry" && tries < MAX_ATTEMPTS) {
        retries.set(key, tries);
        if (!cancelled) timer = window.setTimeout(attempt, CAPTURE_DELAY_MS);
        return;
      }
      retries.delete(key);
      ended.add(key);
      if (!cancelled) endedRef.current?.();
    };
    timer = window.setTimeout(attempt, CAPTURE_DELAY_MS);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [eligible, frameId, getIframe, html, theme]);
}
